import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

type Drop = {
  x: number;
  y: number;
  speed: number;
  len: number;
  hue: number;
  bin: number;
  chars: string[];
  head: number;
};

const GLYPHS =
  'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEF<>{}[]/\\|@#$%';

const drops: Drop[] = [];
let lastW = 0;
let lastH = 0;

function randGlyph(): string {
  return GLYPHS[(Math.random() * GLYPHS.length) | 0]!;
}

function spawnDrop(w: number, h: number, i: number): Drop {
  const len = 8 + ((Math.random() * 18) | 0);
  const chars = Array.from({ length: len }, () => randGlyph());
  return {
    x: Math.random() * w,
    y: Math.random() * h,
    speed: 2 + Math.random() * 6,
    len,
    hue: 145 + Math.random() * 40,
    bin: i % 128,
    chars,
    head: 0,
  };
}

function ensureDrops(w: number, h: number, count: number): void {
  if (w !== lastW || h !== lastH) {
    drops.length = 0;
    lastW = w;
    lastH = h;
  }
  while (drops.length < count) drops.push(spawnDrop(w, h, drops.length));
  while (drops.length > count) drops.pop();
}

export const rainViz: Visualizer = {
  id: 'rain',
  label: 'Matrix Rain',
  draw(frame) {
    const { ctx, w, h, bands, freq, t } = frame;
    // trail — shorter fade on high energy so columns bloom
    clearBg(ctx, w, h, 0.14 + (1 - bands.energy) * 0.14);

    const fontSize = Math.max(11, Math.round(Math.min(w, h) * 0.018));
    const colW = fontSize;
    const maxCols = Math.max(12, Math.floor(w / colW));
    const count = Math.min(
      maxCols,
      Math.floor(18 + bands.energy * maxCols * 0.85 + bands.highs * 12),
    );
    ensureDrops(w, h, count);

    ctx.font = `600 ${fontSize}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // global flash on kick/beat
    if (bands.kick > 0.35 || bands.beat > 0.3) {
      const flash = Math.max(bands.kick, bands.beat);
      ctx.fillStyle = `rgba(40, 255, 140, ${0.04 * flash})`;
      ctx.fillRect(0, 0, w, h);
    }

    for (let i = 0; i < drops.length; i++) {
      const d = drops[i];
      const f = freq[d.bin % freq.length] / 255;
      const fHi = freq[(d.bin * 5 + 17) % freq.length] / 255;

      // speed locked hard to bass/kick/energy + bin
      d.speed =
        (1.2 + f * 10 + bands.bass * 14 + bands.kick * 10 + bands.energy * 6) *
        (0.85 + fHi * 0.5);
      d.y += d.speed * (fontSize * 0.22);

      // mutate glyphs on highs / beat
      if (bands.highs > 0.25 || bands.beat > 0.2 || Math.random() < 0.08 + f * 0.12) {
        const flip = (Math.random() * d.chars.length) | 0;
        d.chars[flip] = randGlyph();
        if (bands.beat > 0.35 && Math.random() < 0.4) {
          d.head = (d.head + 1) % d.chars.length;
        }
      }

      if (d.y - d.chars.length * fontSize > h) {
        const nd = spawnDrop(w, h, i);
        d.x = ((i + Math.random()) / Math.max(1, count)) * w;
        d.y = -Math.random() * h * 0.4;
        d.len = nd.len;
        d.hue = nd.hue;
        d.bin = nd.bin;
        d.chars = nd.chars;
        d.head = 0;
        d.speed = nd.speed;
      }

      const brightBoost = 0.35 + f * 0.65 + bands.energy * 0.25 + bands.beat * 0.2;
      const hue = d.hue + bands.mids * 40 + fHi * 30 + t * 8;

      for (let c = 0; c < d.chars.length; c++) {
        const yy = d.y - c * fontSize;
        if (yy < -fontSize || yy > h + fontSize) continue;
        const headness = 1 - c / d.chars.length;
        const isHead = c === 0;
        const alpha = Math.min(1, (0.12 + headness * 0.9) * brightBoost);
        if (isHead) {
          ctx.fillStyle = `rgba(220,255,230,${Math.min(1, 0.75 + f * 0.25 + bands.kick * 0.2)})`;
          ctx.shadowColor = `hsla(${hue}, 100%, 60%, 0.9)`;
          ctx.shadowBlur = 10 + bands.kick * 18 + f * 8;
        } else {
          ctx.fillStyle = `hsla(${hue}, 100%, ${40 + headness * 35}%, ${alpha})`;
          ctx.shadowBlur = headness > 0.7 ? 4 + f * 6 : 0;
        }
        ctx.fillText(d.chars[c]!, d.x, yy);
      }
      ctx.shadowBlur = 0;
    }

    // bass bloom
    if (bands.bass > 0.3) {
      const g2 = ctx.createRadialGradient(w / 2, h / 2, 10, w / 2, h / 2, Math.min(w, h) * 0.55);
      g2.addColorStop(0, `rgba(40,255,140,${0.04 * bands.bass})`);
      g2.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, w, h);
    }

    drawHud(frame);
  },
};
